import React, { useCallback, useContext, useMemo, useReducer, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import _ from "lodash";
import * as yup from "yup";
import HeadingSection from "../UI/HeadingSection";
import { Form, Formik, useFormikContext } from "formik";
import { Field, FormButtonCluster } from "../UI/forms";
import AlertContext from "../../context/alert/AlertContext";
import Button from "../UI/Button";
import { Body } from "../UI/Text";
import { Flex } from "reflexbox";
import styled, { useTheme } from "styled-components";
import { getPlatforms } from "../../requests/platformManagementRequests";
import { useQuery } from "../Hooks";
import { PageWrapper } from "../UI/Grid";
import Table from "../UI/Table";
import UserContext from "../../context/user/UserContext";
import { createProductList, updateProductList, deleteProductList, getProductList  } from "../../requests/productListManagementRequests";
import { getProducts } from "../../requests/productManagementRequests";
import PagerContextProvider from "../UI/Pager/PagerContext";
import { PagerContext } from "../UI/Pager/PagerContext";

const ButtonsWrapper = styled(Flex)`
  justify-content: center;
  align-items: center;
  transition: 200ms all ease-out;
`;

const CrossHover = styled.span`
  color: ${({color}) => color};
  cursor: pointer;
  font-weight: bold;
  &:hover {
    text-decoration: line-through;
  }
`;

const validationSchema = yup.object().shape({
  pid: yup
    .string("Please select a platform to assign this product list to.")
    .required("Platform is required!"),
  name: yup.string().nullable(),
  add: yup.object().nullable(),
  remove: yup.object().nullable(),
});

const ProductListTable = ({operation, toggleProduct, toggledProducts, hidden}) => {
  const { values } = useFormikContext();
  const { pid } = values;
  const { id: productListId } = useParams();
  const theme = useTheme();
  const pagerContext = useContext(PagerContext);
  const { limit, offset, order, search } = pagerContext;
  const queryVariables = useMemo(() => operation === "add" ? (productListId !== "create" ? { pid, notProductListId: productListId } : { pid }) : { pid, productListId }, [operation, pid, productListId]);
  // useQuery seemed to be tipping off state resets throughout that made this table not work.
  const [data, setData] = useState([]);
  const [isLoading, setLoading] = useState(true);
  useEffect(() => {
    const controller = new AbortController();
    getProducts({ signal: controller.signal, attributes: ["Name", "ID", "ProductNumber", "defaultPrice"], limit, offset, order, search, ...queryVariables}).then(res => {
      setData(res);
      setLoading(false);
    });
    return () => controller.abort();
  }, []);
  
  const { rows = [], count } = data || {};

  const users = useMemo(() => rows.map(row => ({
    ...row,
    toggled: row.ID in (toggledProducts || {})
  })),[rows, toggledProducts]);

  const columns = useMemo(
    () => [
      {
        id: "ID",
        data: "ID",
        header: "Product ID",
        size: { md: 2, sm: -1, xs: -1 },
      },
      {
        id: "Name",
        data: ({ Name, toggled }) => <Body ellipsis bold={toggled}>{Name || "—"}</Body>,
        header: "Product Name",
        size: { md: 3, sm: 3, xs: 5 },
      },
      {
        id: "ProductNumber",
        data: ({ ProductNumber, toggled }) => (
          <Body center ellipsis bold={toggled}>
            {ProductNumber || "—"}
          </Body>
        ),
        header: "Product Number",
        size: { md: 2, sm: -1, xs: -1 },
      },
      {
        id: "defaultPrice",
        data: ({ defaultPrice, toggled }) => (
          <Body center ellipsis bold={toggled}>
            {defaultPrice || "—"}
          </Body>
        ),
        header: "Price",
        size: { md: 2, sm: -1, xs: -1 },
      },
      {
        data: (data) => {
          return (
            <ButtonsWrapper>
              {
                ((operation==="add")!==data.toggled) ? (
                  <Button
                    size="small"
                    color={theme.success}
                    reverse
                    iconName={"faPlus"}
                    onClick={() => toggleProduct(data)}
                  />
                ) : (
                  <Button
                    size="small"
                    color={theme.danger}
                    reverse
                    iconName={"faMinus"}
                    onClick={() => toggleProduct(data)}
                  />
                )
              }
            </ButtonsWrapper>
          );
        },
        header: "Actions",
        sort: false,
        size: { lg: 2, md: 2, sm: 2, xs: 2 },
      },
    ],
    [theme.success, theme.danger, toggleProduct, operation]
  );
  if (hidden) return (<div></div>);
  if (!isLoading && users.length === 0) return (
    <Body bold>No products to {operation}.</Body>
  )
  return (
    <Table
      createButtonText="Create Product"
      createButtonIcon="faUserPlus"
      createLink="/Users/create"
      hideTitle
      hideDescription
      hideRefetch
      data={users}
      hideToggle
      columns={columns}
      hideBack
      hideCreate
      // onRowClick={onRowClick}
      backButtonLink="/Administration"
      // selections={selections}
      isLoading={isLoading}
      totalCount={count}
    />
  );
}

const ProductListFormikForm = ({onDelete}) => {
  const { values, setFieldValue } = useFormikContext();
  const { pid, add, remove } = values;
  const { id: productListId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();

  const [operation, toggleOperation] = useReducer((state,newState)=>state===newState?"":newState,"");

  const {data: platforms, isLoading} = useQuery({
    queryID: "platforms",
    request: getPlatforms,
    forcedFilters: {},
  });
  const platformOptions = useMemo(() => {
    const { rows: _platforms = [] } = platforms || {};
    return _platforms.map(({ id, name }) => ({ value: id, label: name }));
  }, [platforms]);

  const toggleAdd = useCallback((product) => {
    if (product.ID in add) {
      const {[product.ID]: trash, ...rest} = add;
      setFieldValue("add", rest);
    } else {
      setFieldValue("add", {...add, [product.ID]: product});
    }
  }, [add, setFieldValue]);

  const toggleRemove = useCallback((product) => {
    if (product.ID in remove) {
      const {[product.ID]: trash, ...rest} = remove;
      setFieldValue("remove", rest);
    } 
    else {
      setFieldValue("remove", {...remove, [product.ID]: product});
    }
    
  }, [remove, setFieldValue]);

  return (
    <>
      <HeadingSection
        my="1rem"
        hideCreate
        backButtonLink={() => navigate(`/ProductLists/${pid}`)}
        title={`${productListId === "create" ? "Create" : "Edit"} Product List`}
      />
      <Form style={{ width: "100%" }}>
        <Field
          required
          labelText="Assign to Platform"
          name="pid"
          as="select"
        >
          <option key="default" value="">
            Select a Platform
          </option>
          {platformOptions.map(({ label, value }) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </Field>
        <Field labelText="Name" name="name" />

        {
        productListId === "create" ? (
        <Button size="medium" reverse={operation!=="add"}
        color={theme.secondary}
        iconName="faPlus"
        onClick={() => toggleOperation("add")}> Add Products </Button>
        ) :
        (<Flex>
            <Button size="medium" reverse={operation!=="add"}
                    color={theme.secondary}
                    iconName="faPlus"
                    onClick={() => toggleOperation("add")}
                    style={{margin: 0, borderTopRightRadius: 0, borderBottomRightRadius: 0}}> Add Products </Button>
            <Button size="medium" reverse={operation!=="remove"}
                    color={theme.secondary}
                    iconName="faMinus"
                    onClick={() => toggleOperation("remove")}
                    style={{margin: 0, borderTopLeftRadius: 0, borderBottomLeftRadius: 0, marginLeft: -2}}> View / Remove Products </Button>
        </Flex>)
        }
        {!isLoading && (<>
        <PagerContextProvider defaultOrder={["ID", "ASC"]}>
          <ProductListTable operation="add" hidden={operation!=="add"} toggleProduct={toggleAdd} toggledProducts={add}/>
        </PagerContextProvider>
        {(productListId !== "create") && (<PagerContextProvider defaultOrder={["ID", "ASC"]}>
          <ProductListTable operation="remove" hidden={operation!=="remove"} toggleProduct={toggleRemove} toggledProducts={remove}/>
        </PagerContextProvider>)}
        </>)}
        {operation==="" && (<>
          {Object.values(add).map((p) => 
          (<Body>
            <CrossHover onMouseDown={()=>toggleAdd(p)} color="green">
              + {p.Name} ({p.ProductNumber})
            </CrossHover>
          </Body>))}
          {Object.values(remove).map((p) => 
          (<Body>
            <CrossHover onMouseDown={()=>toggleRemove(p)} color="red">
              - {p.Name} ({p.ProductNumber})
            </CrossHover>
          </Body>))}
        </>)}
        
        <FormButtonCluster
          onDelete={onDelete}
          onCancel={() => navigate(`/ProductLists/${pid}`)}
          hideDelete={productListId === "create"}
          recordAction={productListId === "create" ? "create" : "edit"}
          deleteMessage={`Are you sure you want to delete ${values.name}?`}
        />

        {/* <Body>Assigned Users</Body>
        <Flex style={{ gap: "1rem" }} flexWrap="wrap">
          {userOptions}
        </Flex> */}
      </Form>
    </>
  );
};


const ProductListForm = () => {

  const navigate = useNavigate();
  const { id: productListId } = useParams();
  const { showSuccessAlert, showFailureAlert } = useContext(AlertContext);
  const { pid: _pid } = useContext(UserContext);
  const [refresh, setRefresh] = useState(0);
  const queryID = useMemo(() => `productList|${productListId}`, [productListId]);
  const result = useQuery({
    queryID,
    request: getProductList,
    queryVariables: { id: productListId },
    forcedFilters: {},
    skip: !productListId || productListId === 'create',
  });

  const { data: currentProductList, isLoading, refetchQuery } = result || {};
  const {
    pid,
    name,
  } = currentProductList || {};

  const initialValues = useMemo(
    () => ({
      pid: pid || _pid,
      name,
      add: {},
      remove: {}
    }),
    [pid, _pid, name]
  );
  
  
  const onDelete = useCallback(async () => {
    await deleteProductList({ id: productListId });
    showSuccessAlert(`Succesfully Deleted Product List: ${name || productListId}`);
    refetchQuery(`productList|${productListId}`);
    refetchQuery('productLists');
    navigate(`/ProductLists/${pid}`);
  }, [productListId, showSuccessAlert, name, refetchQuery, navigate, pid]);

  
  const onSubmit = useCallback(
    async (_values) => {
      try {
        const { add, remove, ...values} = _values;
        if (productListId === "create") {
          const result = await createProductList({input:values, add: Object.keys(add)});
          refetchQuery('productLists');
          navigate(`/ProductList/${result?.id}`);
          showSuccessAlert(`Successfully created ProductList: ${result?.id}`);
        } else {
          await updateProductList({
            id: productListId,
            input: values,
            add: Object.keys(add),
            remove: Object.keys(remove),
          });
          showSuccessAlert(`Successfully updated ProductList: ${productListId}`);
        }
        refetchQuery(`productList|${productListId}`);
        setRefresh(refresh+1);
      } catch (error) {
        console.error(error);
        showFailureAlert("Failed to create product list.");
      }
    },
    [productListId, refetchQuery, navigate, showSuccessAlert, showFailureAlert, setRefresh, refresh]
  );

  return (
    <PageWrapper>
      <Formik
        onSubmit={onSubmit}
        validationSchema={validationSchema}
        initialValues={initialValues}
        enableReinitialize
        key={refresh}
      >
        {!isLoading && <ProductListFormikForm onDelete={onDelete} />}
      </Formik>
    </PageWrapper>
  )
}

export default ProductListForm;
